import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { ArrowLeft, Plus, Trash2, Download, Save, Power, FileDown, FileWarning, ClipboardList, MapPinned, MapPin, Share2, AlertCircle, AlertTriangle, Building2, Pencil, Printer, QrCode, Package, ChevronDown, ChevronUp, Timer, Play, Pause, Radar, RefreshCw, UserCog, History, ArrowUp, ArrowDown } from "lucide-react";
import { CLIENT_CATEGORIES } from "./Clients";
import { SiteSelect } from "@/components/SiteSelect";
import { ShiftSelect } from "@/components/ShiftSelect";
import { ContractShiftsEditor } from "@/components/ContractShiftsEditor";
import { Time24Select } from "@/components/Time24Select";
import { ClientRulesBadge } from "@/components/ClientRulesBadge";
import { PointLocationPicker } from "@/components/PointLocationPicker";
import { NewsSeverityBadge } from "@/components/NewsSeverityBadge";
import { isOperationalRole, roleLabel } from "@/lib/roles";
import { avgDailyHours, siteHasOwnSchedule, siteEffectiveTotal, sumSitesRealTotal, WEEKDAY_CODES, WEEKDAY_LABELS } from "@/lib/contractShifts";
import { VERIFICATION_MODE_LABEL } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { ExportButtons } from "@/components/ExportButtons"; // Fix BS

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const basePath = isAdmin ? "/admin" : "/supervisor";
  const [client, setClient] = useState(null);
  const [points, setPoints] = useState([]);
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "info");
  const [open, setOpen] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [form, setForm] = useState({ name: "", site_id: "", latitude: "", longitude: "", radius_meters: 50, verification_mode: "qr" });
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [vigiladores, setVigiladores] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [reportRange, setReportRange] = useState({ from: "", to: "" });
  const [reportValidDays, setReportValidDays] = useState(30);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    try {
      const [c, p, s, st, v, sch] = await Promise.all([
        api.get("/clients?include_inactive=true"),
        api.get(`/clients/${id}/patrol-points`),
        api.get("/users/roster?role=supervisor").catch(() => ({ data: [] })),
        api.get(`/clients/${id}/sites`).catch(() => ({ data: [] })),
        api.get("/users/roster").catch(() => ({ data: [] })),
        api.get(`/patrol-schedules?client_id=${id}`).catch(() => ({ data: [] })),
      ]);
      const cli = (c.data || []).find((x) => x.id === id);
      setClient(cli); setInfo(cli ? { ...cli, assigned_supervisor_ids: cli.assigned_supervisor_ids || [], sos_supervisor_id: cli.sos_supervisor_id || "", sos_contact_phone: cli.sos_contact_phone || "", legal_name: cli.legal_name || "", cuit: cli.cuit || "", contract_shifts: cli.contract_shifts || [], contract_total_hours: cli.contract_total_hours ?? "", standard_workday_hours: cli.standard_workday_hours ?? "", sum_enabled: cli.sum_enabled || false, sum_name: cli.sum_name || "", sum_requires_approval: cli.sum_requires_approval !== false, ops_tasks_enabled: cli.ops_tasks_enabled || false, no_service_on_holidays: cli.no_service_on_holidays || false } : cli);
      setPoints(p.data || []);
      setSupervisors(s.data || []);
      setSites(st.data || []);
      setVigiladores((v.data || []).filter((x) => isOperationalRole(x.role) && x.active !== false));
      setSchedules(sch.data || []);
      setAllClients(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadHistory = async () => {
    try { const { data } = await api.get(`/clients/${id}/history`); setHistory(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (tab === "history" && !history) loadHistory(); }, [tab]);

  // Fix W (2026-09-11): entrar desde el listado de "Sedes activas" del
  // inicio con ?tab=sites&site=<id> -- scrollea y resalta esa sede puntual.
  useEffect(() => {
    const siteId = searchParams.get("site");
    if (!siteId || tab !== "sites" || sites.length === 0) return;
    const el = document.querySelector(`[data-testid="site-item-${siteId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.outline = "2px solid #BF3437";
      el.style.outlineOffset = "-2px";
      const t = setTimeout(() => { el.style.outline = ""; }, 2500);
      return () => clearTimeout(t);
    }
  }, [sites, tab, searchParams]);

  const submitPoint = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { name: form.name, site_id: form.site_id || null, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude), radius_meters: parseInt(form.radius_meters), verification_mode: form.verification_mode };
    try {
      if (editingPoint) {
        await api.patch(`/patrol-points/${editingPoint.id}`, payload);
        toast.success("Punto actualizado");
      } else {
        await api.post("/patrol-points", { ...payload, client_id: id });
        toast.success("Punto agregado. QR generado.");
      }
      setOpen(false); setEditingPoint(null); setForm({ name: "", site_id: "", latitude: "", longitude: "", radius_meters: 50, verification_mode: "qr" }); setGpsBusy(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const editPoint = (p) => {
    setEditingPoint(p);
    setForm({ name: p.name, site_id: p.site_id || "", latitude: p.latitude, longitude: p.longitude, radius_meters: p.radius_meters, verification_mode: p.verification_mode || "qr" });
    setGpsBusy(false);
    setOpen(true);
  };

  const delPoint = async (pid) => {
    if (!window.confirm("Si eliminás este punto, los QR impresos del mismo dejarán de funcionar. ¿Continuar?")) return;
    try { await api.delete(`/patrol-points/${pid}`); toast.success("Punto eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const movePoint = async (p, dir) => {
    const group = points.filter((x) => (x.site_id || null) === (p.site_id || null)).sort((a, b) => a.order - b.order);
    const idx = group.findIndex((x) => x.id === p.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= group.length) return;
    const reordered = [...group];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    try {
      await api.post(`/clients/${id}/patrol-points/reorder`, { ordered_ids: reordered.map((x) => x.id) });
      toast.success("Orden actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [gpsBusy, setGpsBusy] = useState(false);
  const tryGetCurrentLocation = () => {
    if (!navigator.geolocation) return toast.error("Tu navegador no soporta geolocalización");
    // Fix K (2026-09-02): antes se llamaba a getCurrentPosition SIN timeout. En celular
    // con datos móviles (sin WiFi, en interiores) el GPS puede no responder nunca, y la
    // llamada quedaba colgada: no completaba, no avisaba y el botón quedaba "presionado".
    // El check-in del supervisor (Checkin.jsx) usa timeout 8000 y por eso sí funciona.
    // Ahora: timeout de 15 s, alta precisión, estado de carga visible y mensajes claros.
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) }));
        setGpsBusy(false);
        const acc = Math.round(pos.coords.accuracy || 0);
        toast.success(acc ? `Coordenadas tomadas de tu GPS (precisión ±${acc} m)` : "Coordenadas tomadas de tu GPS");
      },
      (err) => {
        setGpsBusy(false);
        if (err.code === 1) toast.error("El celular tiene bloqueado el permiso de ubicación para este sitio. Habilitalo en la configuración del navegador y volvé a intentar.");
        else if (err.code === 3) toast.error("El GPS tardó demasiado. Salí a un lugar abierto, activá la ubicación de alta precisión y volvé a intentar, o cargá las coordenadas a mano.");
        else toast.error("No pudimos obtener tu ubicación. Podés cargar las coordenadas a mano.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const toggleInfoSupervisor = (sid) => {
    setInfo((f) => ({
      ...f,
      assigned_supervisor_ids: (f.assigned_supervisor_ids || []).includes(sid)
        ? f.assigned_supervisor_ids.filter((x) => x !== sid)
        : [...(f.assigned_supervisor_ids || []), sid],
    }));
  };

  const saveInfo = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.patch(`/clients/${id}`, {
        name: info.name, address: info.address, contact_name: info.contact_name,
        contact_email: info.contact_email, contact_email2: info.contact_email2, contact_email3: info.contact_email3,
        contact_phone: info.contact_phone, corporate_phone: info.corporate_phone,
        category: info.category || null, assigned_supervisor_ids: info.assigned_supervisor_ids || [],
        sos_supervisor_id: info.sos_supervisor_id || null, sos_contact_phone: info.sos_contact_phone || null,
        legal_name: info.legal_name || null, cuit: info.cuit || null,
        contract_shifts: (info.contract_shifts || []).filter((s) => s.time_from && s.time_to),
        contract_total_hours: info.contract_total_hours === "" ? null : Number(info.contract_total_hours),
        standard_workday_hours: info.standard_workday_hours === "" || info.standard_workday_hours == null ? null : Number(info.standard_workday_hours),
        sum_enabled: !!info.sum_enabled, sum_name: info.sum_name || null, sum_requires_approval: info.sum_requires_approval !== false,
        ops_tasks_enabled: !!info.ops_tasks_enabled,
        no_service_on_holidays: !!info.no_service_on_holidays,
      });
      toast.success("Cliente actualizado");
      if (data.phone_warnings) Object.values(data.phone_warnings).forEach((w) => toast.info(w));
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const toggleActive = async () => {
    if (!window.confirm(client.active ? "¿Desactivar cliente? No aparecerá en selectores nuevos." : "¿Reactivar cliente?")) return;
    try {
      const ep = client.active ? "deactivate" : "activate";
      await api.post(`/clients/${id}/${ep}`);
      toast.success(client.active ? "Cliente desactivado" : "Cliente reactivado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDeletePreview = async () => {
    try {
      const { data } = await api.get(`/clients/${id}/delete-preview`);
      setDeletePreview(data);
      setDeleteText("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (deleteText.trim().toLowerCase() !== client.name.trim().toLowerCase()) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente eliminado definitivamente");
      navigate(`${basePath}/clientes`);
    } catch (e2) { toast.error(formatApiError(e2)); setDeleteBusy(false); }
  };

  const downloadPeriodReport = async () => {
    if (!reportRange.from || !reportRange.to) return toast.error("Elegí desde y hasta");
    try {
      const res = await api.get(`/reports/client-period.pdf?client_id=${id}&date_from=${reportRange.from}&date_to=${reportRange.to}&valid_days=${reportValidDays}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `reporte_${client.name}_${reportRange.from}_${reportRange.to}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success("Reporte descargado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sharePeriodLink = async () => {
    if (!reportRange.from || !reportRange.to) return toast.error("Elegí desde y hasta");
    try {
      const { data } = await api.post(`/clients/${id}/period-verification-link?date_from=${reportRange.from}&date_to=${reportRange.to}&valid_days=${reportValidDays}`);
      await navigator.clipboard.writeText(data.url);
      toast.success(`Link público copiado · válido ${data.expires_in_days} días`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printAllQr = async () => {
    try {
      const res = await api.get(`/clients/${id}/patrol-points/print.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadAllQr = async () => {
    try {
      const res = await api.get(`/clients/${id}/patrol-points/print.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `QR_${client.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!client || !info) return <div style={{ padding: 24 }}><div className="skel" style={{ height: 30, width: 240 }} /></div>;

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <Link to={`${basePath}/clientes`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 16, textDecoration: "none", letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700 }}>
        <ArrowLeft size={14} /> Clientes
      </Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Cliente</div>
          <h1 style={{ fontSize: 34, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>{client.name}</h1>
          <div style={{ color: "var(--invict-text-muted)", fontSize: 13 }}>
            {client.address || "Sin dirección"} · <span className={`badge ${client.active ? "green" : "gray"}`}>{client.active ? "Activo" : "Inactivo"}</span>
            {client.category && (() => {
              const meta = CLIENT_CATEGORIES.find((c) => c.value === client.category);
              return meta ? <> · <span className={`badge ${meta.badge}`} data-testid="detail-category-badge">{meta.label}</span></> : null;
            })()}
            {client.contract_is_24h && <> · <span className="badge green" data-testid="detail-24h-badge">Cobertura 24 hs</span></>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 18, borderBottom: "1px solid var(--invict-bone-3)" }}>
        {[
          { k: "info", label: "Información" },
          { k: "sites", label: `Sedes (${sites.length})` },
          { k: "points", label: `Puntos de ronda (${points.length})` },
          { k: "auto_rondas", label: `Rondas automáticas (${schedules.length})` },
          { k: "coverage", label: "Cobertura de turnos" },
          { k: "materials", label: "Elementos de trabajo" },
          ...(client.sum_enabled ? [{ k: "sum", label: `Reservas ${client.sum_name || "SUM"}` }] : []),
          { k: "history", label: "Historial" },
          { k: "report", label: "Reporte por período" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding: "10px 16px", border: "none", background: "transparent", color: tab === t.k ? "#0A0A0A" : "var(--invict-text-muted)", fontWeight: 700, fontSize: 12, letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer", borderBottom: tab === t.k ? "3px solid #BF3437" : "3px solid transparent" }}
            data-testid={`tab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "info" && (
        <form onSubmit={saveInfo} className="card" style={{ maxWidth: 720, padding: 22 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="field"><label>Nombre del cliente *</label><input required value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} data-testid="info-name"/></div>
            <div className="field"><label>Categoría</label>
              <select value={info.category || ""} onChange={(e) => setInfo({ ...info, category: e.target.value })} data-testid="info-category-select">
                <option value="">Sin categoría</option>
                {CLIENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="field"><label>Dirección</label><input value={info.address || ""} onChange={(e) => setInfo({ ...info, address: e.target.value })} data-testid="info-address"/></div>
            <div className="field"><label>Contacto</label><input value={info.contact_name || ""} onChange={(e) => setInfo({ ...info, contact_name: e.target.value })}/></div>
            <div className="field"><label>Email contacto</label><input type="email" value={info.contact_email || ""} onChange={(e) => setInfo({ ...info, contact_email: e.target.value })} data-testid="info-email"/></div>
            <div className="field"><label>Email adicional</label><input type="email" value={info.contact_email2 || ""} onChange={(e) => setInfo({ ...info, contact_email2: e.target.value })} data-testid="info-email2"/></div>
            <div className="field"><label>Email adicional</label><input type="email" value={info.contact_email3 || ""} onChange={(e) => setInfo({ ...info, contact_email3: e.target.value })} data-testid="info-email3"/></div>
            <div className="field"><label>Teléfono de contacto</label><input value={info.contact_phone || ""} onChange={(e) => setInfo({ ...info, contact_phone: e.target.value })} placeholder="ej. 261 516 9800" data-testid="info-phone"/></div>
            <div className="field"><label>Teléfono corporativo del establecimiento</label><input value={info.corporate_phone || ""} onChange={(e) => setInfo({ ...info, corporate_phone: e.target.value })} placeholder="ej. 261 400 0000" data-testid="info-corporate-phone"/></div>
          </div>
          <div className="field" style={{ marginTop: 4 }}>
            <label>Supervisores asignados <span style={{ fontWeight: 400, color: "var(--invict-text-muted)", textTransform: "none", letterSpacing: 0 }}>(opcional)</span></label>
            <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 8px" }}>Podés dejar el objetivo sin supervisor y asignarlo cuando lo tengas cargado.</p>
            {(() => {
              const staleCount = (info.assigned_supervisor_ids || []).filter((sid) => !supervisors.some((s) => s.id === sid)).length;
              return staleCount > 0 ? (
                <div style={{ fontSize: 11, color: "#d97706", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }} data-testid="info-stale-supervisors-hint">
                  <AlertTriangle size={12} /> {staleCount} referencia(s) a supervisor(es) que ya no están activos -- no se muestran abajo. Limpialas desde Migración / Respaldo.
                </div>
              ) : null;
            })()}
            {supervisors.length === 0 ? (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#0A0A0A", background: "var(--invict-bone-2)", border: "1.5px dashed var(--invict-bone-3)", borderRadius: 2, padding: 10 }} data-testid="info-no-supervisors-hint">
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1, color: "#BF3437" }} />
                <span>Todavía no cargaste supervisores. {isAdmin ? <Link to="/admin/usuarios" style={{ color: "#BF3437", fontWeight: 700 }} data-testid="info-goto-users-link">Crear usuarios →</Link> : "Pedile al admin que cree o asigne un supervisor."}</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2, padding: 8 }} data-testid="info-supervisors-list">
                {supervisors.map((s) => (
                  <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={(info.assigned_supervisor_ids || []).includes(s.id)} onChange={() => toggleInfoSupervisor(s.id)} data-testid={`info-supervisor-checkbox-${s.id}`}/>
                    {s.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="card" style={{ marginTop: 14, padding: 16, background: "rgba(191,52,55,0.05)", border: "1.5px solid rgba(191,52,55,0.25)" }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.05em" }}>SOS de este objetivo</h3>
            <p style={{ color: "var(--invict-text-muted)", fontSize: 12, margin: "0 0 12px" }}>Cuando un vigilador dispare el botón de pánico en este cliente, la alerta se rutea y suena para este supervisor.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div className="field">
                <label>Supervisor responsable del SOS</label>
                <select value={info.sos_supervisor_id || ""} onChange={(e) => setInfo({ ...info, sos_supervisor_id: e.target.value })} data-testid="info-sos-supervisor-select">
                  <option value="">Sin definir (usa el primer supervisor asignado)</option>
                  {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}{(info.assigned_supervisor_ids || []).includes(s.id) ? "" : " (no asignado)"}</option>)}
                </select>
                {info.sos_supervisor_id && !supervisors.some((s) => s.id === info.sos_supervisor_id) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#BF3437", fontWeight: 700, fontSize: 11, marginTop: 6 }} data-testid="info-sos-supervisor-inactive-warning">
                    <AlertTriangle size={13} /> El supervisor guardado ya no está activo. Elegí uno nuevo.
                  </div>
                )}
              </div>
              <div className="field"><label>Teléfono de contacto SOS</label><input value={info.sos_contact_phone || ""} onChange={(e) => setInfo({ ...info, sos_contact_phone: e.target.value })} placeholder="ej. +54 9 261 000 0000" data-testid="info-sos-phone"/></div>
            </div>
          </div>
          <div className="card" style={{ marginTop: 14, padding: 16, background: "var(--invict-bone-2)" }} data-testid="contract-data-block">
            <h3 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.05em" }}>Datos del servicio contratado</h3>
            <p style={{ color: "var(--invict-text-muted)", fontSize: 12, margin: "0 0 12px" }}>Se usan para controlar las horas facturables. El total se calcula solo a partir de las franjas cargadas, pero podés corregirlo a mano si el servicio no es continuo.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div className="field"><label>Razón social</label><input value={info.legal_name || ""} onChange={(e) => setInfo({ ...info, legal_name: e.target.value })} placeholder="Nombre legal, si difiere del objetivo" data-testid="info-legal-name"/></div>
              <div className="field"><label>CUIT</label><input value={info.cuit || ""} onChange={(e) => setInfo({ ...info, cuit: e.target.value })} placeholder="20-30405060-7" data-testid="info-cuit"/></div>
            </div>
            <ContractShiftsEditor shifts={info.contract_shifts} onChange={(s) => setInfo({ ...info, contract_shifts: s })} totalOverride={info.contract_total_hours} onTotalOverrideChange={(v) => setInfo({ ...info, contract_total_hours: v })} testIdPrefix="info-shift" noServiceOnHolidays={info.no_service_on_holidays} onNoServiceOnHolidaysChange={(v) => setInfo({ ...info, no_service_on_holidays: v })}/>
            <div className="field" style={{ marginTop: 10 }}>
              <label>Jornada estándar (hs) <span style={{ fontWeight: 400, color: "var(--invict-text-muted)", textTransform: "none", letterSpacing: 0 }}>(opcional — para calcular horas extra en "Horas Extra")</span></label>
              <input type="number" step="0.5" min="0" value={info.standard_workday_hours ?? ""} onChange={(e) => setInfo({ ...info, standard_workday_hours: e.target.value })} placeholder="ej. 12 (default INVICT)" data-testid="info-standard-workday-hours"/>
              <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>Especialmente útil en objetivos tipo "Obra" (sin turnos fijos): lo que exceda esta jornada se calcula como hora extra.</p>
            </div>
            {(() => {
              const clientTotal = info.contract_total_hours !== "" && info.contract_total_hours != null ? Number(info.contract_total_hours) : avgDailyHours(info.contract_shifts);
              const realTotal = sumSitesRealTotal(sites, clientTotal, info.contract_shifts);
              if (realTotal == null) return null;
              return (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--invict-bone-3)", display: "flex", alignItems: "center", gap: 8 }} data-testid="info-real-total-sedes">
                  <Building2 size={13} style={{ color: "#BF3437" }} />
                  <span style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Total real del objetivo (sumando sedes con horario propio):</span>
                  <b style={{ fontSize: 13, color: "#0A0A0A" }}>{realTotal} h/día</b>
                </div>
              );
            })()}
          </div>
          <ClientRulesBadge clientId={id} />
          <div className="card" style={{ marginTop: 14, padding: 16, background: "var(--invict-bone-2)" }} data-testid="sum-config-block">
            <h3 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.05em" }}>SUM / salón de usos múltiples</h3>
            <p style={{ color: "var(--invict-text-muted)", fontSize: 12, margin: "0 0 12px" }}>Si lo activás, los usuarios "cliente" de este objetivo pueden reservarlo desde su portal, y el vigilador de turno ve las reservas del día en su pantalla de inicio.</p>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: info.sum_enabled ? 12 : 0 }}>
              <input type="checkbox" checked={!!info.sum_enabled} onChange={(e) => setInfo({ ...info, sum_enabled: e.target.checked })} data-testid="info-sum-enabled"/>
              Habilitar reserva del SUM para este objetivo
            </label>
            {info.sum_enabled && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div className="field"><label>Nombre del espacio</label><input value={info.sum_name || ""} onChange={(e) => setInfo({ ...info, sum_name: e.target.value })} placeholder="ej. SUM, Salón de fiestas, Quincho" data-testid="info-sum-name"/></div>
                <div className="field">
                  <label>Aprobación</label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginTop: 8 }}>
                    <input type="checkbox" checked={info.sum_requires_approval !== false} onChange={(e) => setInfo({ ...info, sum_requires_approval: e.target.checked })} data-testid="info-sum-requires-approval"/>
                    Requiere aprobación de cliente_admin/admin antes de confirmarse
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="card" style={{ marginTop: 14, padding: 16, background: "var(--invict-bone-2)" }} data-testid="ops-tasks-config-block">
            <h3 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tareas operativas (retail)</h3>
            <p style={{ color: "var(--invict-text-muted)", fontSize: 12, margin: "0 0 12px" }}>Si lo activás, los vigiladores de este objetivo pueden registrar control de tickets, recuperos, devoluciones, decomisos, alarmados y perfiles de producto por sede.</p>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={!!info.ops_tasks_enabled} onChange={(e) => setInfo({ ...info, ops_tasks_enabled: e.target.checked })} data-testid="info-ops-tasks-enabled"/>
              Habilitar Tareas operativas para este objetivo
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button type="submit" className="btn-primary" disabled={busy} data-testid="info-save"><Save size={14} style={{ marginRight: 6 }}/>Guardar cambios</button>
            {client.active && <button type="button" className="btn-ghost" onClick={toggleActive} style={{ color: "#BF3437", borderColor: "#BF3437" }} data-testid="client-deactivate"><Power size={12} style={{ marginRight: 4 }}/>Desactivar cliente</button>}
            <button type="button" className="btn-ghost" onClick={openDeletePreview} style={{ color: "#BF3437", borderColor: "#BF3437", marginLeft: "auto" }} data-testid="client-delete-open"><Trash2 size={12} style={{ marginRight: 4 }}/>Eliminar cliente definitivamente</button>
          </div>
        </form>
      )}

      {tab === "sites" && <SitesTab clientId={id} sites={sites} onChange={load} clientShifts={info.contract_shifts} clientTotal={info.contract_total_hours !== "" && info.contract_total_hours != null ? Number(info.contract_total_hours) : avgDailyHours(info.contract_shifts)} roster={vigiladores} />}

      {tab === "points" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: 0 }}>Puntos físicos a vigilar</h2>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {points.length > 0 && (
                <>
                  <button onClick={printAllQr} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }} data-testid="points-print-all"><Printer size={13} style={{ marginRight: 6 }} />Imprimir todos los QR</button>
                  <button onClick={downloadAllQr} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }} data-testid="points-download-all"><Download size={13} style={{ marginRight: 6 }} />Descargar todos</button>
                </>
              )}
              <button className="btn-primary" onClick={() => { setEditingPoint(null); setForm({ name: "", site_id: "", latitude: "", longitude: "", radius_meters: 50, verification_mode: "qr" }); setGpsBusy(false); setOpen(true); }} data-testid="point-new"><Plus size={14} style={{ marginRight: 6 }} />Agregar punto</button>
            </div>
          </div>
          <div className="card" style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 16px", marginBottom: 14, background: "var(--invict-bone-2)" }} data-testid="points-circuit-info">
            <QrCode size={20} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12, color: "var(--invict-text-muted)", lineHeight: 1.5 }}>
              <b style={{ color: "#0A0A0A" }}>Cada punto genera su QR automáticamente al crearlo.</b> Circuito: se crea el punto con su ubicación GPS → se imprime el QR → se pega en el lugar → el vigilador lo escanea en la ronda y queda registrada la posición. Elegí "Cercanía GPS" si el punto se marca sin QR pegado. Usá "Imprimir todos los QR" para una hoja lista para recortar y pegar en cada ubicación.
              <br/><b style={{ color: "#0A0A0A" }}>El orden (#1, #2, #3...) define la secuencia obligatoria de la ronda</b> — escanear fuera de ese orden genera una anomalía. Usá las flechas ↑/↓ de cada punto para reordenar sin borrar y recrear: el QR ya impreso sigue siendo válido, solo cambia el número.
            </div>
          </div>
          {points.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 40, color: "var(--invict-text-muted)" }}>
              Este cliente no tiene puntos de ronda. Agregá el primero para asignarle rondas a vigiladores.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
              {points.map((p) => {
                const group = points.filter((x) => (x.site_id || null) === (p.site_id || null)).sort((a, b) => a.order - b.order);
                const gIdx = group.findIndex((x) => x.id === p.id);
                return (
                <div key={p.id} className="card" style={{ display: "flex", gap: 14, alignItems: "center", padding: 16 }} data-testid={`point-${p.id}`}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                    <button type="button" onClick={() => movePoint(p, -1)} disabled={gIdx === 0} className="btn-ghost" style={{ padding: "2px 4px" }} title="Subir" data-testid={`point-move-up-${p.id}`}><ArrowUp size={11} /></button>
                    <button type="button" onClick={() => movePoint(p, 1)} disabled={gIdx === group.length - 1} className="btn-ghost" style={{ padding: "2px 4px" }} title="Bajar" data-testid={`point-move-down-${p.id}`}><ArrowDown size={11} /></button>
                  </div>
                  <img src={`${BACKEND}/api/patrol-points/${p.id}/qr.png`} alt={`QR ${p.name}`} style={{ width: 84, height: 84, background: "var(--invict-bone-2)", padding: 4, borderRadius: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, letterSpacing: "0.15em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Punto #{p.order}{p.site_name ? ` · ${p.site_name}` : ""}</div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>{p.latitude.toFixed(5)}, {p.longitude.toFixed(5)} · radio {p.radius_meters}m</div>
                    <span data-testid={`point-verification-${p.id}`} style={{ display: "inline-block", marginTop: 6, fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 20, background: (p.verification_mode || "qr") === "qr" ? "#e8e8e8" : "#e6f3ea", color: (p.verification_mode || "qr") === "qr" ? "#555" : "#1c7a3d" }}>{VERIFICATION_MODE_LABEL[p.verification_mode || "qr"]}</span>
                    <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                      <a href={`${BACKEND}/api/patrol-points/${p.id}/qr.png`} download={`QR_${p.name}.png`} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10 }}><Download size={11} style={{ marginRight: 4 }} />QR</a>
                      <button onClick={() => editPoint(p)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10 }} data-testid={`point-edit-${p.id}`}>Editar</button>
                      <button onClick={() => delPoint(p.id)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`point-delete-${p.id}`}><Trash2 size={11} /></button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === "auto_rondas" && <AutoPatrolsTab clientId={id} sites={sites} schedules={schedules} onChange={load} />}

      {tab === "coverage" && <CoverageTab clientId={id} clientName={client.name} vigiladores={vigiladores} allClients={allClients} />}

      {tab === "materials" && <MaterialsCatalog clientId={id} sites={sites} />}

      {tab === "sum" && <SumReservationsTab clientId={id} clientName={client.name} sumName={client.sum_name || "SUM"} />}

      {tab === "history" && (
        <div>
          {history == null && <div className="skel" style={{ height: 100 }} />}
          {history && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="max-md:!grid-cols-1">
              <div className="card" style={{ padding: 16 }}>
                <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}><MapPinned size={12} style={{ display: "inline", marginRight: 4, color: "#BF3437" }}/>Rondas ({history.patrols.length})</h3>
                {history.patrols.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin rondas registradas.</div>}
                {history.patrols.slice(0, 12).map((p) => (
                  <Link to={`${basePath}/rondas/${p.id}`} key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)", textDecoration: "none", color: "inherit" }}>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ fontWeight: 700, color: "#0A0A0A" }}>{p.vigilador_name}</div>
                      <div style={{ color: "var(--invict-text-muted)", fontSize: 10 }}>{new Date(p.scheduled_for).toLocaleString("es-AR")}</div>
                    </div>
                    <span className={`badge ${p.status === "completada" ? "green" : p.status === "en_curso" ? "blue" : "gray"}`}>{p.points_completed}/{p.total_points}</span>
                  </Link>
                ))}
              </div>
              <div className="card" style={{ padding: 16 }}>
                <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}><FileWarning size={12} style={{ display: "inline", marginRight: 4, color: "#BF3437" }}/>Novedades ({history.news.length})</h3>
                {history.news.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin novedades.</div>}
                {history.news.slice(0, 12).map((n) => (
                  <div key={n.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#0A0A0A" }}>{n.title}</div>
                      <NewsSeverityBadge news={n} />
                    </div>
                    <div style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>{n.supervisor_name} · {new Date(n.created_at).toLocaleString("es-AR")}</div>
                  </div>
                ))}
              </div>
              <div className="card" style={{ padding: 16 }}>
                <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}><ClipboardList size={12} style={{ display: "inline", marginRight: 4, color: "#BF3437" }}/>Servicios ({history.services.length})</h3>
                {history.services.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin servicios registrados.</div>}
                {history.services.slice(0, 12).map((s) => (
                  <div key={s.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: "#0A0A0A" }}>{s.supervisor_name}</div>
                    <div style={{ color: "var(--invict-text-muted)", fontSize: 10 }}>Abierto {new Date(s.opened_at).toLocaleString("es-AR")}{s.closed_at ? ` · Cerrado ${new Date(s.closed_at).toLocaleString("es-AR")}` : " · ABIERTO"}</div>
                  </div>
                ))}
              </div>
              <div className="card" style={{ padding: 16 }}>
                <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}><MapPin size={12} style={{ display: "inline", marginRight: 4, color: "#BF3437" }}/>Marcaciones ({history.checkins.length})</h3>
                {history.checkins.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin marcaciones.</div>}
                {history.checkins.slice(0, 12).map((c) => (
                  <div key={c.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: "#0A0A0A" }}>{c.supervisor_name}</div>
                    <div style={{ color: "var(--invict-text-muted)", fontSize: 10 }}>Ingreso {new Date(c.checkin_at).toLocaleString("es-AR")}{c.checkout_at ? ` · Egreso ${new Date(c.checkout_at).toLocaleString("es-AR")}` : " · ABIERTO"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "report" && (
        <div className="card" style={{ maxWidth: 540, padding: 22 }}>
          <h3 style={{ margin: "0 0 8px", fontWeight: 800, color: "#0A0A0A" }}>Reporte consolidado</h3>
          <p style={{ color: "var(--invict-text-muted)", fontSize: 13, marginBottom: 16 }}>Genera un PDF con todos los servicios, novedades y rondas del cliente en el rango de fechas elegido. Lo podés enviar al cliente final.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field"><label>Desde</label><input type="date" value={reportRange.from} onChange={(e) => setReportRange({ ...reportRange, from: e.target.value })} data-testid="report-from"/></div>
            <div className="field"><label>Hasta</label><input type="date" value={reportRange.to} onChange={(e) => setReportRange({ ...reportRange, to: e.target.value })} data-testid="report-to"/></div>
          </div>
          <div className="field">
            <label>Vigencia del link público</label>
            <select value={reportValidDays} onChange={(e) => setReportValidDays(parseInt(e.target.value))} data-testid="report-valid-days">
              <option value={30}>30 días</option>
              <option value={60}>60 días</option>
              <option value={90}>90 días</option>
            </select>
          </div>
          <button onClick={downloadPeriodReport} className="btn-primary" style={{ width: "100%", marginBottom: 8 }} data-testid="report-generate"><FileDown size={14} style={{ marginRight: 6 }}/>Generar reporte PDF</button>
          <button onClick={sharePeriodLink} className="btn-ghost" style={{ width: "100%" }} data-testid="report-share-link"><Share2 size={14} style={{ marginRight: 6 }}/>Copiar link público (válido {reportValidDays} días)</button>
        </div>
      )}

      {open && (
        <div className="modal-overlay" onClick={() => { setOpen(false); setGpsBusy(false); }}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitPoint}>
            <h3>{editingPoint ? "Editar punto" : "Agregar punto de ronda"}</h3>
            <p>{editingPoint ? "Cambiá los datos del punto. El QR existente se mantiene." : "El sistema generará un QR único. Imprimilo y pegalo en el sitio físico."}</p>
            <div className="field"><label>Nombre del punto *</label><input required placeholder="ej. Portón sur" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="point-name"/></div>
            <SiteSelect clientId={id} value={form.site_id} onChange={(v) => setForm({ ...form, site_id: v })} testIdPrefix="point"/>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Latitud *</label><input required type="number" step="any" placeholder="-34.6037" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="point-lat"/></div>
              <div className="field"><label>Longitud *</label><input required type="number" step="any" placeholder="-58.3816" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="point-lng"/></div>
            </div>
            {/* Fix K (2026-09-02): estado de carga visible mientras el GPS responde, para que
                no parezca que el botón quedó trabado. */}
            <button type="button" className="btn-ghost" onClick={tryGetCurrentLocation} disabled={gpsBusy} style={{ width: "100%", marginBottom: 8 }} data-testid="point-use-gps">
              {gpsBusy ? "Buscando tu ubicación… (puede tardar unos segundos)" : "Usar mi ubicación actual"}
            </button>
            {/* Fix L (2026-09-02): mapa para ubicar el punto tocando la posición exacta. El GPS
                del celular a veces devuelve una posición "trabada" muy lejana, y así el punto
                quedaba mal ubicado y el vigilador después no podía marcarlo por cercanía. */}
            <PointLocationPicker
              latitude={form.latitude}
              longitude={form.longitude}
              radiusMeters={form.radius_meters}
              onPick={(lat, lng) => setForm((f) => ({ ...f, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }))}
              existingPoints={points}
            />
            <div className="field"><label>Radio de tolerancia (metros)</label><input type="number" min="5" max="1000" value={form.radius_meters} onChange={(e) => setForm({ ...form, radius_meters: e.target.value })} data-testid="point-radius"/></div>
            <div className="field">
              <label>Cómo se marca este punto</label>
              <select value={form.verification_mode} onChange={(e) => setForm({ ...form, verification_mode: e.target.value })} data-testid="point-verification-mode">
                <option value="qr">QR (escanear el código pegado)</option>
                <option value="gps">Cercanía GPS (sin QR)</option>
                <option value="ambos">Ambos (QR o cercanía GPS)</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => { setOpen(false); setGpsBusy(false); }}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="point-submit">{busy ? "Guardando..." : (editingPoint ? "Guardar cambios" : "Crear y generar QR")}</button>
            </div>
          </form>
        </div>
      )}

      {deletePreview && (
        <div className="modal-overlay" onClick={() => { setDeletePreview(null); setDeleteText(""); }}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={confirmDelete} data-testid="client-delete-modal">
            <h3 style={{ color: "#BF3437", display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={18}/>Eliminar "{deletePreview.client_name}" definitivamente</h3>
            <p style={{ fontSize: 13 }}>
              Esta acción es <b>irreversible</b>. Se van a borrar <b>{deletePreview.total_records}</b> registros asociados a este objetivo:
            </p>
            <div style={{ background: "var(--invict-bone-2)", padding: 12, borderRadius: 2, marginBottom: 12, fontSize: 12, lineHeight: 1.7 }} data-testid="client-delete-counts">
              <div>Puntos de ronda + QR: <b>{deletePreview.patrol_points}</b></div>
              <div>Rondas: <b>{deletePreview.patrols}</b> · Escaneos: <b>{deletePreview.patrol_scans}</b></div>
              <div>Novedades: <b>{deletePreview.news}</b> · Servicios: <b>{deletePreview.services}</b> · Marcaciones: <b>{deletePreview.supervisor_checkins}</b></div>
              <div>Alertas de pánico: <b>{deletePreview.panic_alerts}</b></div>
              <div>Elementos de trabajo: <b>{deletePreview.material_items + deletePreview.material_handoffs}</b> · Personal autorizado: <b>{deletePreview.authorized_persons}</b></div>
              <div>Vehículos: <b>{deletePreview.vehicle_logs}</b> · Visitas: <b>{deletePreview.visit_logs}</b> · Mensajes de chat: <b>{deletePreview.chat_messages}</b></div>
              <div>Turnos de cronograma: <b>{deletePreview.schedule_assignments}</b></div>
              {deletePreview.users_with_default_target > 0 && (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--invict-bone-3)", color: "#BF3437", fontWeight: 700 }}>
                  <AlertCircle size={12} style={{ marginRight: 4, display: "inline", verticalAlign: "text-top" }}/>
                  {deletePreview.users_with_default_target} usuario(s) tienen este objetivo como asignado por defecto. Se les va a quitar para que ningún SOS quede sin destino.
                </div>
              )}
            </div>
            <div className="field">
              <label>Para confirmar, escribí el nombre exacto: <b>{deletePreview.client_name}</b></label>
              <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus data-testid="client-delete-confirm-input"/>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => { setDeletePreview(null); setDeleteText(""); }}>Cancelar</button>
              <button type="submit" className="btn-primary" style={{ background: "#BF3437" }}
                disabled={deleteBusy || deleteText.trim().toLowerCase() !== deletePreview.client_name.trim().toLowerCase()}
                data-testid="client-delete-confirm-submit">
                {deleteBusy ? "Eliminando..." : "Eliminar definitivamente"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SitesTab({ clientId, sites, onChange, clientShifts, clientTotal, roster = [] }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", contract_shifts: [], contract_total_hours: "", assigned_vigilador_ids: [], no_service_on_holidays: false });
  const [busy, setBusy] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [expandedSite, setExpandedSite] = useState(null);
  const [matName, setMatName] = useState("");
  const [matDate, setMatDate] = useState(""); // Fix BO: fecha de entrega del elemento
  const [matBusy, setMatBusy] = useState(false);

  const openNew = () => { setEditing(null); setForm({ name: "", address: "", contract_shifts: [], contract_total_hours: "", assigned_vigilador_ids: [], no_service_on_holidays: false }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, address: s.address || "", contract_shifts: s.contract_shifts || [], contract_total_hours: s.contract_total_hours ?? "", assigned_vigilador_ids: s.assigned_vigilador_ids || [], no_service_on_holidays: s.no_service_on_holidays || false }); setOpen(true); };
  const toggleFormPerson = (uid) => setForm((f) => ({ ...f, assigned_vigilador_ids: f.assigned_vigilador_ids.includes(uid) ? f.assigned_vigilador_ids.filter((x) => x !== uid) : [...f.assigned_vigilador_ids, uid] }));

  const loadMaterials = async () => {
    try { const { data } = await api.get(`/clients/${clientId}/materials`); setMaterials(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadMaterials(); }, [clientId]);

  const toggleSiteMaterials = (siteId) => { setExpandedSite(expandedSite === siteId ? null : siteId); setMatName(""); setMatDate(""); };

  const addSiteMaterial = async (siteId) => {
    const value = matName.trim();
    if (!value) return;
    if (!matDate) { toast.error("Indicá la fecha de entrega del elemento"); return; }
    setMatBusy(true);
    try {
      await api.post(`/clients/${clientId}/materials`, { name: value, site_id: siteId, delivered_at: matDate });
      setMatName(""); setMatDate(""); await loadMaterials(); toast.success("Elemento agregado a la sede");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMatBusy(false); }
  };

  const removeSiteMaterial = async (mid) => {
    if (!window.confirm("¿Eliminar este elemento de la sede?")) return;
    try { await api.delete(`/materials/${mid}`); await loadMaterials(); toast.success("Elemento eliminado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = {
      name: form.name, address: form.address,
      contract_shifts: (form.contract_shifts || []).filter((s) => s.time_from && s.time_to),
      contract_total_hours: form.contract_total_hours === "" ? null : Number(form.contract_total_hours),
      assigned_vigilador_ids: form.assigned_vigilador_ids || [],
      no_service_on_holidays: !!form.no_service_on_holidays,
    };
    try {
      if (editing) await api.patch(`/sites/${editing.id}`, payload);
      else await api.post(`/clients/${clientId}/sites`, payload);
      toast.success(editing ? "Sede actualizada" : "Sede agregada");
      setOpen(false); onChange();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar la sede "${s.name}"? Los registros ya cargados con esta sede la conservan como referencia histórica.`)) return;
    try { await api.delete(`/sites/${s.id}`); toast.success("Sede eliminada"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: 0 }}>Sedes del cliente</h2>
          <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>Si el servicio de este cliente se ramifica en varias direcciones, cargalas acá. Van a poder elegirse al crear rondas, puntos, visitas, novedades y servicios.</p>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="site-new"><Plus size={14} style={{ marginRight: 6 }} />Agregar sede</button>
      </div>
      {sites.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 40, color: "var(--invict-text-muted)" }} data-testid="sites-empty">
          Este cliente no tiene sedes cargadas. Sin sedes, se asume una sede única (funciona igual que hoy).
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {sites.map((s) => {
            const ownSchedule = siteHasOwnSchedule(s);
            const total = siteEffectiveTotal(s, clientTotal, clientShifts);
            const siteMaterials = materials.filter((m) => m.site_id === s.id);
            const isExpanded = expandedSite === s.id;
            return (
              <div key={s.id} style={{ borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`site-item-${s.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Building2 size={16} style={{ color: "#BF3437" }} />
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{s.address || "Sin dirección"}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                        {ownSchedule ? (
                          <span className="badge blue" data-testid={`site-own-schedule-${s.id}`}>Horario propio</span>
                        ) : (
                          <span className="badge gray" data-testid={`site-inherited-schedule-${s.id}`}>Hereda horario del objetivo</span>
                        )}
                        {total != null && <span style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{total} h/día</span>}
                        {s.contract_is_24h && <span className="badge green" data-testid={`site-24h-badge-${s.id}`}>24 hs</span>}
                      </div>
                      {(s.assigned_vigilador_ids || []).length > 0 && (
                        <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }} data-testid={`site-assigned-personnel-${s.id}`}>
                          Personal asignado: <b style={{ color: "#0A0A0A" }}>{s.assigned_vigilador_ids.map((uid) => roster.find((r) => r.id === uid)?.name || "—").join(", ")}</b>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => toggleSiteMaterials(s.id)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }} data-testid={`site-materials-toggle-${s.id}`}>
                      <Package size={11}/> Elementos ({siteMaterials.length}) {isExpanded ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
                    </button>
                    <button onClick={() => openEdit(s)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10 }} data-testid={`site-edit-${s.id}`}><Pencil size={11}/></button>
                    <button onClick={() => remove(s)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`site-delete-${s.id}`}><Trash2 size={11}/></button>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ padding: "0 20px 16px", background: "var(--invict-bone-2)" }} data-testid={`site-materials-panel-${s.id}`}>
                    <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "10px 0" }}>Elementos propios de esta sede — se suman a los generales del objetivo en el checklist del vigilador cuando está en esta sede.</p>
                    {siteMaterials.length === 0 ? (
                      <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 10 }} data-testid={`site-materials-empty-${s.id}`}>Sin elementos propios todavía.</div>
                    ) : (
                      siteMaterials.map((m) => (
                        <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 13 }} data-testid={`site-material-item-${m.id}`}>
                          <span style={{ fontWeight: 700 }}>{m.name}{m.delivered_at && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: "var(--invict-text-muted)" }}>Entregado el {m.delivered_at.split("-").reverse().join("/")}</span>}</span>
                          <button onClick={() => removeSiteMaterial(m.id)} className="btn-ghost" style={{ padding: "3px 8px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`site-material-remove-${m.id}`}>Eliminar</button>
                        </div>
                      ))
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <input value={matName} onChange={(e) => setMatName(e.target.value)} placeholder="ej. Llave portón trasero" style={{ flex: 1, padding: "8px 10px", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2 }} data-testid={`site-material-add-name-${s.id}`}/>
                      <input type="date" value={matDate} onChange={(e) => setMatDate(e.target.value)} title="Fecha de entrega" style={{ padding: "8px 10px", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2 }} data-testid={`site-material-add-date-${s.id}`}/>
                      <button onClick={() => addSiteMaterial(s.id)} disabled={matBusy || !matName.trim() || !matDate} className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }} data-testid={`site-material-add-submit-${s.id}`}>Agregar</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h3>{editing ? "Editar sede" : "Agregar sede"}</h3>
            <div className="field"><label>Nombre de la sede *</label><input required placeholder="ej. Sucursal Centro" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-name-input"/></div>
            <div className="field"><label>Dirección</label><input placeholder="ej. San Martín 450" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="site-address-input"/></div>
            <div className="card" style={{ padding: 14, margin: "4px 0 14px", background: "var(--invict-bone-2)" }} data-testid="site-contract-block">
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#0A0A0A", marginBottom: 6 }}>Horario propio de esta sede</div>
              <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 10px" }}>
                Opcional. Si no cargás franjas acá, esta sede muestra y usa el horario del objetivo ({clientTotal != null ? `${clientTotal} h/día` : "sin horario cargado"}).
              </p>
              <ContractShiftsEditor shifts={form.contract_shifts} onChange={(sh) => setForm({ ...form, contract_shifts: sh })} totalOverride={form.contract_total_hours} onTotalOverrideChange={(v) => setForm({ ...form, contract_total_hours: v })} testIdPrefix="site-shift" noServiceOnHolidays={form.no_service_on_holidays} onNoServiceOnHolidaysChange={(v) => setForm({ ...form, no_service_on_holidays: v })}/>
            </div>
            <div className="field">
              <label>Personal asignado a esta sede <span style={{ fontWeight: 400, color: "var(--invict-text-muted)", textTransform: "none", letterSpacing: 0 }}>(opcional)</span></label>
              <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 8px" }}>Se sugiere en Cronograma y se muestra en "Mis Servicios" del supervisor.</p>
              {roster.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }} data-testid="site-no-personnel-hint">Todavía no hay personal operativo cargado.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2, padding: 8 }} data-testid="site-personnel-list">
                  {roster.map((u) => (
                    <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={form.assigned_vigilador_ids.includes(u.id)} onChange={() => toggleFormPerson(u.id)} data-testid={`site-personnel-checkbox-${u.id}`}/>
                      {u.name} <span className="badge gray" style={{ fontSize: 9 }}>{roleLabel(u.role).toUpperCase()}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="site-submit">{busy ? "Guardando..." : (editing ? "Guardar cambios" : "Agregar sede")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Fix A (2026-08-28): propietario del elemento (INVICT/CLIENTE/SIN ESPECIFICAR).
const MATERIAL_OWNER_META = {
  invict: { label: "INVICT", badge: "red" },
  cliente: { label: "CLIENTE", badge: "blue" },
  sin_especificar: { label: "SIN ESPECIFICAR", badge: "gray" },
};

function MaterialsCatalog({ clientId, sites }) {
  const [items, setItems] = useState([]);
  const [handoffs, setHandoffs] = useState(null);
  const [name, setName] = useState("");
  const [siteId, setSiteId] = useState("");
  const [scope, setScope] = useState("all");
  const [owner, setOwner] = useState("sin_especificar");
  const [deliveredAt, setDeliveredAt] = useState(""); // Fix BO: fecha de entrega (obligatoria al agregar)
  const [historyFilterSite, setHistoryFilterSite] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/clients/${clientId}/materials`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadHandoffs = async () => {
    try { const { data } = await api.get(`/material-handoffs?client_id=${clientId}`); setHandoffs(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); loadHandoffs(); }, [clientId]);

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/clients/${clientId}/materials`, { name, site_id: siteId || null, scope: siteId ? "all" : scope, owner, delivered_at: deliveredAt }); setName(""); setOwner("sin_especificar"); setDeliveredAt(""); load(); toast.success("Elemento agregado"); }
    catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  const remove = async (mid) => {
    if (!window.confirm("¿Eliminar este elemento del catálogo?")) return;
    try { await api.delete(`/materials/${mid}`); load(); toast.success("Elemento eliminado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const changeOwner = async (mid, newOwner) => {
    try { await api.patch(`/materials/${mid}`, { owner: newOwner }); load(); toast.success("Propietario actualizado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const changeDelivered = async (mid, value) => {
    try { await api.patch(`/materials/${mid}`, { delivered_at: value || null }); load(); toast.success("Fecha de entrega actualizada"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const centralOnlyItems = items.filter((it) => !it.site_id && it.scope === "central_only");
  const wholeObjectiveItems = items.filter((it) => !it.site_id && it.scope !== "central_only");
  const bySite = sites.map((s) => ({ site: s, items: items.filter((it) => it.site_id === s.id) }));
  const filteredHandoffs = handoffs ? handoffs.filter((h) => !historyFilterSite || (historyFilterSite === "general" ? !h.site_id : h.site_id === historyFilterSite)) : null;
  const hasSites = sites.length > 0;

  // Fix A (2026-08-28): numeración continua respetando el orden en que se listan los grupos.
  let runningIndex = 0;
  const nextStart = (count) => { const start = runningIndex + 1; runningIndex += count; return start; };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="card" style={{ maxWidth: 640, padding: 22 }} data-testid="materials-catalog-card">
        <h3 style={{ margin: "0 0 6px", fontWeight: 800, color: "#0A0A0A" }}>Catálogo de elementos de trabajo del cliente</h3>
        <p style={{ color: "var(--invict-text-muted)", fontSize: 13, marginBottom: 16 }}>
          Definí qué elementos de trabajo recibe y entrega el vigilador en cada turno (celular, radio, botiquín, llaves, etc).
          {hasSites ? " Los elementos del cliente central y los de cada sede NO son los mismos: elegí \"Solo cliente central\" para que aparezca únicamente cuando el vigilador NO está en ninguna sede puntual, \"Todo el objetivo (central + sedes)\" para que se sume siempre, o una sede específica para que sea propio de ella." : " Sin sedes cargadas, todos los elementos son del objetivo."}
        </p>
        <form onSubmit={add} style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="ej. Radio Motorola" required style={{ flex: "1 1 200px", padding: "10px 12px", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2 }} data-testid="mat-add-name"/>
          {hasSites && (
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="mat-add-site" style={{ flex: "1 1 180px" }}>
              <option value="">Sin sede específica (cliente central)</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{`Sede: ${s.name}`}</option>)}
            </select>
          )}
          {hasSites && !siteId && (
            <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="mat-add-scope" style={{ flex: "1 1 220px" }}>
              <option value="all">Todo el objetivo (central + sedes)</option>
              <option value="central_only">Solo cliente central</option>
            </select>
          )}
          <select value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="mat-add-owner" style={{ flex: "1 1 180px" }}>
            <option value="sin_especificar">Propietario: Sin especificar</option>
            <option value="invict">Propietario: INVICT</option>
            <option value="cliente">Propietario: Cliente</option>
          </select>
          <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, color: "var(--invict-text-muted)", flex: "0 1 160px" }}>
            Fecha de entrega *
            <input type="date" required value={deliveredAt} onChange={(e) => setDeliveredAt(e.target.value)} data-testid="mat-add-delivered"/>
          </label>
          <button type="submit" className="btn-primary" disabled={busy || !name || !deliveredAt} data-testid="mat-add-submit">Agregar</button>
        </form>

        {items.length === 0 ? (
          <div style={{ color: "var(--invict-text-muted)", fontSize: 13, textAlign: "center", padding: 20 }} data-testid="mat-empty">Sin elementos de trabajo. Agregá el primero.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {hasSites ? (
              <>
                <MaterialsGroup label="Solo cliente central" list={centralOnlyItems} onRemove={remove} onChangeOwner={changeOwner} onChangeDelivered={changeDelivered} testid="central-only" startIndex={nextStart(centralOnlyItems.length)} />
                <MaterialsGroup label="Todo el objetivo (central + sedes)" list={wholeObjectiveItems} onRemove={remove} onChangeOwner={changeOwner} onChangeDelivered={changeDelivered} testid="whole-objective" startIndex={nextStart(wholeObjectiveItems.length)} />
              </>
            ) : (
              <MaterialsGroup label="Elementos del objetivo" list={wholeObjectiveItems} onRemove={remove} onChangeOwner={changeOwner} onChangeDelivered={changeDelivered} testid="whole-objective" startIndex={nextStart(wholeObjectiveItems.length)} />
            )}
            {bySite.map(({ site, items: siteItems }) => siteItems.length > 0 && (
              <MaterialsGroup key={site.id} label={`Sede: ${site.name}`} list={siteItems} onRemove={remove} onChangeOwner={changeOwner} onChangeDelivered={changeDelivered} testid={site.id} startIndex={nextStart(siteItems.length)} />
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ maxWidth: 720, padding: 22 }} data-testid="materials-history-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontWeight: 800, color: "#0A0A0A" }}>Historial de checklist (cumplimiento)</h3>
          <ExportButtons kind="handoffs" rows={filteredHandoffs || []} testIdPrefix="materials-history"
            filtersLabel={historyFilterSite ? (historyFilterSite === "general" ? "Sin sede (general)" : `Sede: ${sites.find((s) => s.id === historyFilterSite)?.name || ""}`) : "Todas las sedes"} />
          {sites.length > 0 && (
            <select value={historyFilterSite} onChange={(e) => setHistoryFilterSite(e.target.value)} data-testid="materials-history-site-filter">
              <option value="">Todas las sedes</option>
              <option value="general">Sin sede (general)</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
        {filteredHandoffs == null ? (
          <div className="skel" style={{ height: 60 }} />
        ) : filteredHandoffs.length === 0 ? (
          <div style={{ color: "var(--invict-text-muted)", fontSize: 13, textAlign: "center", padding: 20 }} data-testid="materials-history-empty">Sin registros de checklist todavía.</div>
        ) : (
          <div>
            {filteredHandoffs.slice(0, 20).map((h) => (
              <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`materials-history-${h.id}`}>
                <div>
                  <span style={{ fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h.direction === "recepcion" ? "Recepción" : "Entrega"}</span> · {h.user_name}
                  <span style={{ color: "var(--invict-text-muted)" }}> · {h.site_name || "General"}</span>
                  <div style={{ color: "var(--invict-text-muted)", fontSize: 10 }}>{new Date(h.created_at).toLocaleString("es-AR")}</div>
                </div>
                {h.has_missing && <span className="badge red">FALTANTE</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MaterialsGroup({ label, list, onRemove, onChangeOwner, onChangeDelivered, testid, startIndex = 1 }) {
  return (
    <div data-testid={`mat-group-${testid}`}>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--invict-text-muted)", marginBottom: 6 }}>{label} ({list.length})</div>
      {list.length === 0 ? (
        <div style={{ color: "var(--invict-text-muted)", fontSize: 12, padding: "6px 0" }}>Sin elementos.</div>
      ) : (
        list.map((it, idx) => {
          const ownerKey = it.owner || "sin_especificar";
          const ownerMeta = MATERIAL_OWNER_META[ownerKey] || MATERIAL_OWNER_META.sin_especificar;
          return (
            <div key={it.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--invict-bone-3)", gap: 10, flexWrap: "wrap" }} data-testid={`mat-item-${it.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: "var(--invict-text-muted)", minWidth: 20 }} data-testid={`mat-item-index-${it.id}`}>{startIndex + idx}.</span>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{it.name}</span>
                <span className={`badge ${ownerMeta.badge}`} data-testid={`mat-item-owner-${it.id}`}>{ownerMeta.label}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "var(--invict-text-muted)" }} title="Fecha de entrega">
                  Entregado
                  <input type="date" value={it.delivered_at || ""} onChange={(e) => onChangeDelivered(it.id, e.target.value)} data-testid={`mat-item-delivered-${it.id}`} style={{ fontSize: 11, padding: "3px 4px" }}/>
                </label>
                <select value={ownerKey} onChange={(e) => onChangeOwner(it.id, e.target.value)} data-testid={`mat-item-owner-select-${it.id}`} style={{ fontSize: 11, padding: "4px 6px" }}>
                  <option value="sin_especificar">Sin especificar</option>
                  <option value="invict">INVICT</option>
                  <option value="cliente">Cliente</option>
                </select>
                <button onClick={() => onRemove(it.id)} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`mat-remove-${it.id}`}>Eliminar</button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

const EMPTY_SCHEDULE_FORM = { site_id: "", name: "", schedule_mode: "interval", time_from: "08:00", time_to: "20:00", interval_minutes: 60, specific_times: ["22:00"], days_of_week: null, tolerance_minutes: 60, active: true };
const WEEKDAY_SHORT_ES = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
function fmtPreviewOcc(occ) {
  const d = new Date(`${occ.ar_date}T00:00:00`);
  const [, mo, da] = occ.ar_date.split("-");
  return `${WEEKDAY_SHORT_ES[d.getDay()]} ${da}/${mo} ${occ.ar_time}`;
}

function AutoPatrolsTab({ clientId, sites, schedules, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SCHEDULE_FORM);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [reassignBusy, setReassignBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openNew = () => { setEditing(null); setForm(EMPTY_SCHEDULE_FORM); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({
    site_id: s.site_id || "", name: s.name || "",
    schedule_mode: s.schedule_mode || "interval",
    time_from: s.time_from || "08:00", time_to: s.time_to || "20:00", interval_minutes: s.interval_minutes || 60,
    specific_times: s.specific_times && s.specific_times.length ? s.specific_times : ["22:00"],
    days_of_week: s.days_of_week, tolerance_minutes: s.tolerance_minutes, active: s.active,
  }); setOpen(true); };

  const setSpecificTime = (idx, v) => setForm((f) => ({ ...f, specific_times: f.specific_times.map((t, i) => (i === idx ? v : t)) }));
  const addSpecificTime = () => setForm((f) => ({ ...f, specific_times: [...f.specific_times, "05:00"] }));
  const removeSpecificTime = (idx) => setForm((f) => ({ ...f, specific_times: f.specific_times.filter((_, i) => i !== idx) }));

  const toggleDay = (code) => {
    setForm((f) => {
      const cur = f.days_of_week || [];
      const next = cur.includes(code) ? cur.filter((d) => d !== code) : [...cur, code];
      return { ...f, days_of_week: next.length ? next : null };
    });
  };

  // Prompt AG - Cambio 4 (2026-09-23): vista previa de solo lectura de las
  // próximas 6 rondas, con los mismos valores del formulario SIN guardar
  // nada -- se recalcula cada vez que cambia algo relevante del formulario.
  useEffect(() => {
    if (!open) return;
    const isInterval = form.schedule_mode === "interval";
    if (isInterval && (!form.time_from || !form.time_to || !form.interval_minutes)) { setPreview(null); return; }
    if (!isInterval && !(form.specific_times || []).filter(Boolean).length) { setPreview(null); return; }
    const params = new URLSearchParams({ client_id: clientId, schedule_mode: form.schedule_mode });
    if (form.site_id) params.set("site_id", form.site_id);
    if (isInterval) {
      params.set("time_from", form.time_from); params.set("time_to", form.time_to); params.set("interval_minutes", String(form.interval_minutes));
    } else {
      params.set("specific_times", form.specific_times.filter(Boolean).join(","));
    }
    if (form.days_of_week && form.days_of_week.length) params.set("days_of_week", form.days_of_week.join(","));
    if (editing) params.set("schedule_id", editing.id);
    const t = setTimeout(() => {
      setPreviewLoading(true);
      api.get(`/patrol-schedules/preview?${params.toString()}`)
        .then(({ data }) => setPreview(data.occurrences))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [open, clientId, editing, form.site_id, form.schedule_mode, form.time_from, form.time_to, form.interval_minutes, JSON.stringify(form.specific_times), JSON.stringify(form.days_of_week)]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const isInterval = form.schedule_mode === "interval";
    const payload = {
      site_id: form.site_id || null, name: form.name, schedule_mode: form.schedule_mode,
      tolerance_minutes: parseInt(form.tolerance_minutes), days_of_week: form.days_of_week, active: form.active,
      ...(isInterval
        ? { time_from: form.time_from, time_to: form.time_to, interval_minutes: parseInt(form.interval_minutes), specific_times: null }
        : { specific_times: form.specific_times.filter(Boolean), time_from: null, time_to: null, interval_minutes: null }),
    };
    try {
      if (editing) {
        await api.patch(`/patrol-schedules/${editing.id}`, payload);
        toast.success("Programación actualizada");
      } else {
        const { data } = await api.post("/patrol-schedules", { ...payload, client_id: clientId });
        toast.success(data.warning ? data.warning : "Programación creada. Las rondas se generan solas.");
      }
      setOpen(false); onChange();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (s) => {
    try { await api.patch(`/patrol-schedules/${s.id}`, { active: !s.active }); toast.success(s.active ? "Programación pausada" : "Programación activada"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar la programación "${s.name || s.time_from + '-' + s.time_to}"? Las rondas ya generadas se conservan.`)) return;
    try { await api.delete(`/patrol-schedules/${s.id}`); toast.success("Programación eliminada"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const generateNow = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/patrol-schedules/generate-now");
      toast.success(`Generación disparada: ${data.created} ronda(s) nueva(s) creada(s).`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenBusy(false); }
  };

  const reassignPending = async () => {
    if (!window.confirm("¿Recalcular el vigilador asignado de las rondas PENDIENTES de este objetivo según el cronograma actual? Las rondas ya iniciadas o completadas no se tocan.")) return;
    setReassignBusy(true);
    try {
      const { data } = await api.post(`/patrol-schedules/reassign-pending?client_id=${clientId}`);
      toast.success(`Revisadas ${data.checked} ronda(s): ${data.reassigned} reasignada(s) (${data.en_curso_reasignadas} en curso), ${data.unassigned} sin asignar. En curso intactas (con escaneos): ${data.en_curso_intactas}.`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReassignBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: 0 }}>Rondas automáticas</h2>
          <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>Definí una ventana horaria y cada cuánto se repite: el sistema genera solo las rondas y las asigna al vigilador de turno. Si nadie está de turno, queda "sin asignar" para que administración la asigne.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={generateNow} disabled={genBusy} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }} data-testid="schedule-generate-now"><Radar size={13} style={{ marginRight: 6 }} />{genBusy ? "Generando..." : "Generar ahora"}</button>
          <button onClick={reassignPending} disabled={reassignBusy} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }} data-testid="schedule-reassign-pending"><RefreshCw size={13} style={{ marginRight: 6 }} />{reassignBusy ? "Reasignando..." : "Reasignar pendientes"}</button>
          <button className="btn-primary" onClick={openNew} data-testid="schedule-new"><Plus size={14} style={{ marginRight: 6 }} />Nueva programación</button>
        </div>
      </div>

      {schedules.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 40, color: "var(--invict-text-muted)" }} data-testid="schedules-empty">
          Este cliente no tiene programaciones de rondas automáticas. Las rondas cargadas a mano siguen funcionando igual.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {schedules.map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`schedule-item-${s.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Timer size={16} style={{ color: "#BF3437" }} />
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>{s.name || "Programación"}{s.site_name ? ` · ${s.site_name}` : ""}</div>
                  <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>
                    {s.schedule_mode === "specific_times"
                      ? `Horarios: ${(s.specific_times || []).join(", ")}`
                      : `${s.time_from} a ${s.time_to} · cada ${s.interval_minutes} min`} · tolerancia {s.tolerance_minutes} min
                  </div>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>{s.days_of_week ? s.days_of_week.map((d) => WEEKDAY_LABELS[d] || d).join(", ") : "Todos los días"}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                    <span className={s.active ? "badge green" : "badge gray"} data-testid={`schedule-status-${s.id}`}>{s.active ? "Activa" : "Pausada"}</span>
                    {s.last_generation_note && <span style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>Última generación: {s.last_generation_note}</span>}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => toggleActive(s)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10 }} data-testid={`schedule-toggle-${s.id}`}>{s.active ? <Pause size={11}/> : <Play size={11}/>}</button>
                <button onClick={() => openEdit(s)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10 }} data-testid={`schedule-edit-${s.id}`}><Pencil size={11}/></button>
                <button onClick={() => remove(s)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`schedule-delete-${s.id}`}><Trash2 size={11}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h3>{editing ? "Editar programación" : "Nueva programación de rondas"}</h3>
            <p>El sistema genera las rondas de esta ventana horaria solas, sin cargarlas una por una.</p>
            <div className="field"><label>Nombre (opcional)</label><input placeholder="ej. Rondas diurnas" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="schedule-name" /></div>
            {sites.length > 0 && (
              <div className="field">
                <label>Sede (opcional)</label>
                <select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} data-testid="schedule-site">
                  <option value="">Todo el objetivo (sin sede específica)</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div className="field">
              <label>Modo de programación</label>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" onClick={() => setForm({ ...form, schedule_mode: "interval" })} data-testid="schedule-mode-interval"
                  style={{ flex: 1, padding: "8px 10px", fontSize: 11, fontWeight: 800, borderRadius: 4, border: "1px solid var(--invict-bone-3)", cursor: "pointer", background: form.schedule_mode === "interval" ? "#0A0A0A" : "transparent", color: form.schedule_mode === "interval" ? "#FFFBF4" : "#0A0A0A" }}>
                  Por intervalo
                </button>
                <button type="button" onClick={() => setForm({ ...form, schedule_mode: "specific_times" })} data-testid="schedule-mode-specific"
                  style={{ flex: 1, padding: "8px 10px", fontSize: 11, fontWeight: 800, borderRadius: 4, border: "1px solid var(--invict-bone-3)", cursor: "pointer", background: form.schedule_mode === "specific_times" ? "#0A0A0A" : "transparent", color: form.schedule_mode === "specific_times" ? "#FFFBF4" : "#0A0A0A" }}>
                  Por horarios específicos
                </button>
              </div>
            </div>
            {form.schedule_mode === "interval" ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div className="field"><label>Desde *</label><Time24Select required value={form.time_from} onChange={(v) => setForm({ ...form, time_from: v })} testId="schedule-time-from" /></div>
                  <div className="field"><label>Hasta *</label><Time24Select required value={form.time_to} onChange={(v) => setForm({ ...form, time_to: v })} testId="schedule-time-to" /></div>
                </div>
                <div className="field"><label>Repetir cada (minutos) *</label><input required type="number" min="5" max="720" value={form.interval_minutes} onChange={(e) => setForm({ ...form, interval_minutes: e.target.value })} data-testid="schedule-interval" /></div>
              </>
            ) : (
              <div className="field" data-testid="schedule-specific-times">
                <label>Horarios exactos * (ej. 22:00 y 05:00)</label>
                {form.specific_times.map((t, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                    <Time24Select required value={t} onChange={(v) => setSpecificTime(idx, v)} testId={`schedule-specific-time-${idx}`} />
                    {form.specific_times.length > 1 && (
                      <button type="button" onClick={() => removeSpecificTime(idx)} data-testid={`schedule-specific-time-remove-${idx}`}
                        style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "6px 10px", cursor: "pointer", color: "#BF3437" }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addSpecificTime} className="btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} data-testid="schedule-specific-time-add">
                  <Plus size={12} style={{ marginRight: 4 }} />Agregar horario
                </button>
              </div>
            )}
            <div className="field"><label>Tolerancia (minutos)</label><input type="number" min="5" max="240" value={form.tolerance_minutes} onChange={(e) => setForm({ ...form, tolerance_minutes: e.target.value })} data-testid="schedule-tolerance" /></div>
            <div className="field">
              <label>Días (vacío = todos los días)</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {WEEKDAY_CODES.map((code) => (
                  <button type="button" key={code} onClick={() => toggleDay(code)} data-testid={`schedule-day-${code}`}
                    style={{ padding: "6px 10px", fontSize: 11, fontWeight: 700, borderRadius: 4, border: "1px solid var(--invict-bone-3)", cursor: "pointer", background: (form.days_of_week || []).includes(code) ? "#BF3437" : "transparent", color: (form.days_of_week || []).includes(code) ? "#fff" : "var(--invict-text-muted)" }}>
                    {WEEKDAY_LABELS[code]}
                  </button>
                ))}
              </div>
            </div>
            <div className="field" data-testid="schedule-preview">
              <label>Vista previa: próximas 6 rondas</label>
              {previewLoading && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Calculando...</div>}
              {!previewLoading && (!preview || preview.length === 0) && (
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Completá los horarios para ver la vista previa.</div>
              )}
              {!previewLoading && preview && preview.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {preview.map((occ, idx) => (
                    <div key={idx} data-testid={`schedule-preview-item-${idx}`} style={{ fontSize: 12, padding: "5px 8px", borderRadius: 4, background: (occ.covered_by || (occ.overlaps_with || []).length || (occ.takes_over_from || []).length) ? "#fdf1e0" : "var(--invict-bone-2)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 700 }}>{fmtPreviewOcc(occ)}</span>
                        {occ.covered_by && <span style={{ fontSize: 10, color: "#a67c00", fontWeight: 700 }} data-testid={`schedule-preview-covered-${idx}`}>no se genera: la genera {occ.covered_by}, que empieza a esa hora</span>}
                      </div>
                      {(occ.overlaps_with || []).length > 0 && !occ.covered_by && (
                        <div style={{ fontSize: 10, color: "#a67c00", fontWeight: 700, marginTop: 2 }} data-testid={`schedule-preview-overlap-${idx}`}>
                          coincide con {occ.overlaps_with.join(", ")}: se van a generar dos rondas
                        </div>
                      )}
                      {(occ.takes_over_from || []).length > 0 && (
                        <div style={{ fontSize: 10, color: "#a67c00", fontWeight: 700, marginTop: 2 }} data-testid={`schedule-preview-takesover-${idx}`}>
                          la ronda de las {occ.ar_time} de {occ.takes_over_from.join(", ")} deja de generarse; la genera esta
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, margin: "10px 0" }}>
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="schedule-active" /> Activa
            </label>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="schedule-submit">{busy ? "Guardando..." : (editing ? "Guardar cambios" : "Crear programación")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function CoverageTab({ clientId, clientName, vigiladores, allClients }) {
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changing, setChanging] = useState(null);
  const [form, setForm] = useState({ new_user_id: "", time_from: "", time_to: "", justification: "" });
  const [busy, setBusy] = useState(false);
  const [blockedInfo, setBlockedInfo] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [dayOff, setDayOff] = useState([]);
  const [adHoc, setAdHoc] = useState(null);
  const [adHocBlocked, setAdHocBlocked] = useState(null);
  const [adHocBusy, setAdHocBusy] = useState(false);
  const [reprocessBusy, setReprocessBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const month = date.slice(0, 7);
      const { data } = await api.get(`/schedule?month=${month}&client_id=${clientId}`);
      setRows((data.assignments || []).filter((a) => a.date === date));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId, date]);

  const loadDayOff = async () => {
    try { const { data } = await api.get(`/schedule/day-off?date=${date}`); setDayOff(data || []); }
    catch { setDayOff([]); }
  };
  useEffect(() => { loadDayOff(); /* eslint-disable-next-line */ }, [date]);

  const loadHistory = async () => {
    try { const { data } = await api.get(`/schedule/coverage-changes?client_id=${clientId}`); setHistory(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory, clientId]);

  const openChange = (a) => {
    setChanging(a);
    setBlockedInfo(null);
    setForm({ new_user_id: "", time_from: a.is_day_off ? "" : a.time_from, time_to: a.is_day_off ? "" : a.time_to, justification: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.new_user_id) return toast.error("Elegí quién va a cubrir el turno");
    if (changing.is_day_off && (!form.time_from || !form.time_to)) return toast.error("Este turno era franco: indicá el horario de la cobertura");
    setBusy(true);
    try {
      const payload = { new_user_id: form.new_user_id, justification: form.justification.trim() || null };
      if (changing.is_day_off) { payload.time_from = form.time_from; payload.time_to = form.time_to; }
      const { data } = await api.post(`/schedule/${changing.id}/coverage-change`, payload);
      toast.success("Cobertura asignada");
      if (data.reassignment) toast.info(data.reassignment.message, 8000);
      (data.warnings || []).forEach((w) => toast.info(w.message, 6000));
      setChanging(null); setBlockedInfo(null);
      load(); loadDayOff();
      if (showHistory) loadHistory();
    } catch (e2) {
      const msg = formatApiError(e2);
      if (e2?.response?.status === 422) setBlockedInfo(msg);
      toast.error(msg, 7000);
    } finally { setBusy(false); }
  };

  const openAdHoc = () => {
    setAdHocBlocked(null);
    setAdHoc({ user_id: "", client_id: clientId, site_id: "", shift_choice: "manual", time_from: "", time_to: "", justification: "" });
  };

  const submitAdHoc = async (e) => {
    e.preventDefault();
    if (!adHoc.user_id) return toast.error("Elegí a quién vas a asignar");
    if (!adHoc.client_id) return toast.error("Elegí el objetivo donde va a cubrir");
    if (!adHoc.time_from || !adHoc.time_to) return toast.error("Elegí una franja horaria o cargala a mano");
    setAdHocBusy(true);
    try {
      const { data } = await api.post("/schedule/coverage-assign", {
        user_id: adHoc.user_id, date, client_id: adHoc.client_id, site_id: adHoc.site_id || null,
        time_from: adHoc.time_from, time_to: adHoc.time_to, justification: adHoc.justification.trim() || null,
      });
      toast.success("Cobertura asignada");
      if (data.reassignment) toast.info(data.reassignment.message, 8000);
      (data.warnings || []).forEach((w) => toast.info(w.message, 6000));
      setAdHoc(null); setAdHocBlocked(null);
      load(); loadDayOff();
      if (showHistory) loadHistory();
    } catch (e2) {
      const msg = formatApiError(e2);
      if (e2?.response?.status === 422) setAdHocBlocked(msg);
      toast.error(msg, 7000);
    } finally { setAdHocBusy(false); }
  };

  const reprocessCoverage = async () => {
    if (!window.confirm(`¿Reasignar las rondas de las ${history.length} cobertura(s) ya registradas de este objetivo? Las rondas con escaneos no se tocan.`)) return;
    setReprocessBusy(true);
    try {
      const { data } = await api.post(`/schedule/coverage-changes/reprocess?client_id=${clientId}`);
      toast.success(data.message, 8000);
      load(); loadDayOff();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReprocessBusy(false); }
  };

  return (
    <div data-testid="coverage-tab">
      <div className="card" style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 16px", marginBottom: 14, background: "var(--invict-bone-2)" }}>
        <UserCog size={20} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: "var(--invict-text-muted)", lineHeight: 1.5 }}>
          <b style={{ color: "#0A0A0A" }}>Cubrí ausencias sin tocar el Cronograma.</b> Elegí un día, cambiá quién cubre un turno de este objetivo y guardá. El Cronograma sigue siendo la plantilla base; esto es una excepción puntual, marcada y auditada.
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Día</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="coverage-date" />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary" onClick={openAdHoc} style={{ padding: "8px 14px", fontSize: 11 }} data-testid="coverage-adhoc-open">
            <UserCog size={13} style={{ marginRight: 6 }} />Asignar franco a otro objetivo
          </button>
          <button className="btn-ghost" onClick={() => setShowHistory((v) => !v)} style={{ padding: "8px 14px", fontSize: 11 }} data-testid="coverage-history-toggle">
            <History size={13} style={{ marginRight: 6 }} />{showHistory ? "Ocultar historial" : "Ver historial de cobertura"}
          </button>
        </div>
      </div>

      {dayOff.length > 0 && (
        <div className="card" style={{ padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "var(--invict-text-muted)" }} data-testid="coverage-dayoff-today">
          <b style={{ color: "#0A0A0A" }}>De franco el {date}:</b> {dayOff.map((d) => `${d.user_name} (${d.client_name})`).join(", ")}. Podés asignar a cualquiera de ellos a cubrir en cualquier objetivo con "Asignar franco a otro objetivo".
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)" }}>Cargando...</div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)" }} data-testid="coverage-empty">
          No hay turnos cargados en el Cronograma para este objetivo el {date}.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {rows.map((a) => (
            <div key={a.id} data-testid={`coverage-row-${a.id}`} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10,
              padding: "14px 16px", borderBottom: "1px solid var(--invict-bone-3)",
              borderLeft: a.is_coverage_override ? "4px solid #BF3437" : "4px solid transparent",
              background: a.is_coverage_override ? "#fdf3f3" : "transparent",
            }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>
                  {a.user_name}{a.site_name ? ` · ${a.site_name}` : ""}
                  {a.is_coverage_override && (
                    <span className="badge red" style={{ marginLeft: 8 }} data-testid={`coverage-badge-${a.id}`}>
                      <AlertTriangle size={10} style={{ marginRight: 3, verticalAlign: "-1px" }} />Cobertura
                    </span>
                  )}
                  {a.day_off_worked && (
                    <span className="badge purple" style={{ marginLeft: 6 }} data-testid={`coverage-dayoffworked-${a.id}`}>Franco trabajado</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 3 }}>
                  {a.is_day_off ? "FRANCO" : `${a.time_from} a ${a.time_to}`}
                  {a.is_coverage_override && a.original_user_name && ` · reemplazó a ${a.original_user_name}`}
                  {a.day_off_worked && a.day_off_worked_from_client_name && ` · tenía franco en ${a.day_off_worked_from_client_name}`}
                </div>
                {a.is_coverage_override && a.coverage_reason && (
                  <div style={{ fontSize: 11, color: "#BF3437", marginTop: 3 }}>Observación: {a.coverage_reason}</div>
                )}
              </div>
              <button className="btn-ghost" onClick={() => openChange(a)} style={{ padding: "7px 12px", fontSize: 11 }} data-testid={`coverage-change-${a.id}`}>
                <UserCog size={12} style={{ marginRight: 6 }} />Cambiar vigilador
              </button>
            </div>
          ))}
        </div>
      )}

      {showHistory && (
        <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 16 }} data-testid="coverage-history-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)" }}>
            <span style={{ fontWeight: 800, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Historial de cobertura — {clientName}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={reprocessCoverage} disabled={reprocessBusy || history.length === 0} className="btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} data-testid="coverage-history-reprocess">
                <RefreshCw size={12} style={{ marginRight: 6 }} />{reprocessBusy ? "Reasignando..." : "Reasignar rondas de este historial"}
              </button>
              <a className="btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} href={`${BACKEND}/api/schedule/coverage-changes/export.csv?client_id=${clientId}`} target="_blank" rel="noreferrer" data-testid="coverage-history-export">
                <FileDown size={12} style={{ marginRight: 6 }} />Exportar CSV
              </a>
            </div>
          </div>
          {history.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 12 }}>Sin cambios de cobertura registrados todavía.</div>
          ) : (
            history.map((h) => (
              <div key={h.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`coverage-history-item-${h.id}`}>
                {h.coverage_kind === "ad_hoc" ? (
                  <><b style={{ color: "#0A0A0A" }}>{h.changed_by_name}</b> asignó a <b>{h.new_user_name}</b> a cubrir el {h.date} {h.time_from}–{h.time_to}{h.site_name ? ` (${h.site_name})` : ""} (asignación ad-hoc, sin reemplazar un turno puntual).</>
                ) : (
                  <><b style={{ color: "#0A0A0A" }}>{h.changed_by_name}</b> cambió a <b>{h.previous_user_name}</b> por <b>{h.new_user_name}</b> el {h.date} {h.time_from}–{h.time_to}{h.site_name ? ` (${h.site_name})` : ""}.</>
                )}
                <div style={{ color: "var(--invict-text-muted)", marginTop: 2 }}>{new Date(h.changed_at).toLocaleString("es-AR")}{h.day_off_worked ? ` · franco trabajado (tenía franco en ${h.day_off_worked_from_client_name})` : ""}</div>
                {h.justification && <div style={{ color: "#BF3437", marginTop: 2 }}>Justificación: {h.justification}</div>}
                {(h.warnings || []).map((w, i) => <div key={i} style={{ color: "#a67c00", marginTop: 2 }}>Advertencia: {w.message}</div>)}
              </div>
            ))
          )}
        </div>
      )}

      {changing && (
        <div className="modal-overlay" onClick={() => setChanging(null)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h3>Cambiar vigilador de este turno</h3>
            <p>
              {changing.is_day_off ? "FRANCO" : `${changing.time_from} a ${changing.time_to}`} · originalmente asignado a <b>{changing.user_name}</b>
              {changing.site_name ? ` · ${changing.site_name}` : ""}
            </p>
            <div className="field">
              <label>Nueva persona *</label>
              <select required value={form.new_user_id} onChange={(e) => { setForm({ ...form, new_user_id: e.target.value }); setBlockedInfo(null); }} data-testid="coverage-new-user">
                <option value="">Elegí quién cubre</option>
                {vigiladores.filter((v) => v.id !== changing.user_id).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            {changing.is_day_off && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div className="field"><label>Desde *</label><Time24Select required value={form.time_from} onChange={(v) => setForm({ ...form, time_from: v })} testId="coverage-time-from" /></div>
                <div className="field"><label>Hasta *</label><Time24Select required value={form.time_to} onChange={(v) => setForm({ ...form, time_to: v })} testId="coverage-time-to" /></div>
              </div>
            )}
            <div className="field">
              <label>Observación {blockedInfo ? "(obligatoria para forzar el cambio)" : "(opcional)"}</label>
              <textarea rows={3} value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })}
                placeholder="Ej: cubre porque el titular está de licencia médica" data-testid="coverage-justification"
                style={blockedInfo ? { borderColor: "#BF3437" } : {}} />
            </div>
            {blockedInfo && (
              <div className="card" style={{ background: "#fdf3f3", border: "1px solid #BF3437", padding: "10px 12px", fontSize: 12, color: "#BF3437", marginBottom: 10 }} data-testid="coverage-blocked-info">
                <AlertTriangle size={13} style={{ marginRight: 6, verticalAlign: "-2px" }} />{blockedInfo}
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setChanging(null)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="coverage-submit">{busy ? "Guardando..." : "Confirmar cobertura"}</button>
            </div>
          </form>
        </div>
      )}

      {adHoc && (
        <div className="modal-overlay" onClick={() => setAdHoc(null)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitAdHoc} data-testid="coverage-adhoc-modal">
            <h3>Asignar franco a otro objetivo</h3>
            <p>Elegí a alguien (típicamente de franco ese día) y mandalo a cubrir en CUALQUIER objetivo, en cualquiera de sus franjas contratadas. No hace falta que ya exista un turno cargado ahí.</p>
            <div className="field">
              <label>Persona *</label>
              <select required value={adHoc.user_id} onChange={(e) => setAdHoc({ ...adHoc, user_id: e.target.value })} data-testid="coverage-adhoc-user">
                <option value="">Elegí quién va a cubrir</option>
                {dayOff.length > 0 && (
                  <optgroup label="De franco hoy">
                    {dayOff.map((d) => <option key={d.id} value={d.user_id}>{d.user_name} (franco en {d.client_name})</option>)}
                  </optgroup>
                )}
                <optgroup label="Todo el personal">
                  {vigiladores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </optgroup>
              </select>
            </div>
            <div className="field">
              <label>Objetivo *</label>
              <select required value={adHoc.client_id} onChange={(e) => setAdHoc({ ...adHoc, client_id: e.target.value, site_id: "", shift_choice: "manual" })} data-testid="coverage-adhoc-client">
                {allClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <SiteSelect clientId={adHoc.client_id} value={adHoc.site_id} onChange={(v) => setAdHoc({ ...adHoc, site_id: v, shift_choice: "manual" })} testIdPrefix="coverage-adhoc" />
            <ShiftSelect clientId={adHoc.client_id} siteId={adHoc.site_id} clients={allClients} value={adHoc.shift_choice}
              onSelectShift={(s) => setAdHoc((f) => (s
                ? { ...f, shift_choice: s.id, time_from: s.time_from, time_to: s.time_to }
                : { ...f, shift_choice: "manual" }))}
              testIdPrefix="coverage-adhoc" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Desde *</label><Time24Select required disabled={adHoc.shift_choice !== "manual"} value={adHoc.time_from} onChange={(v) => setAdHoc({ ...adHoc, time_from: v })} testId="coverage-adhoc-time-from" style={adHoc.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined} /></div>
              <div className="field"><label>Hasta *</label><Time24Select required disabled={adHoc.shift_choice !== "manual"} value={adHoc.time_to} onChange={(v) => setAdHoc({ ...adHoc, time_to: v })} testId="coverage-adhoc-time-to" style={adHoc.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined} /></div>
            </div>
            <div className="field">
              <label>Observación {adHocBlocked ? "(obligatoria para forzar el cambio)" : "(opcional)"}</label>
              <textarea rows={3} value={adHoc.justification} onChange={(e) => setAdHoc({ ...adHoc, justification: e.target.value })}
                placeholder="Ej: cubre porque el titular está de licencia médica" data-testid="coverage-adhoc-justification"
                style={adHocBlocked ? { borderColor: "#BF3437" } : {}} />
            </div>
            {adHocBlocked && (
              <div className="card" style={{ background: "#fdf3f3", border: "1px solid #BF3437", padding: "10px 12px", fontSize: 12, color: "#BF3437", marginBottom: 10 }} data-testid="coverage-adhoc-blocked-info">
                <AlertTriangle size={13} style={{ marginRight: 6, verticalAlign: "-2px" }} />{adHocBlocked}
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setAdHoc(null)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={adHocBusy} data-testid="coverage-adhoc-submit">{adHocBusy ? "Guardando..." : "Confirmar cobertura"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SumReservationsTab({ clientId, sumName }) {
  const monthKey = (d) => d.toISOString().slice(0, 7);
  const [month, setMonth] = useState(monthKey(new Date()));
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/sum-reservations?client_id=${clientId}&month=${month}`); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [clientId, month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(monthKey(new Date(y, m - 1 + delta, 1)));
  };

  const decide = async (rid, approve, reason) => {
    setBusy(rid);
    try {
      await api.patch(`/sum-reservations/${rid}/decision`, { approve, reason });
      toast.success(approve ? "Reserva aprobada" : "Reserva rechazada");
      setRejecting(null); setRejectReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const cancel = async (rid) => {
    if (!window.confirm("¿Cancelar esta reserva?")) return;
    setBusy(rid);
    try { await api.delete(`/sum-reservations/${rid}`); toast.success("Reserva cancelada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const STATUS_BADGE = { confirmada: "green", pendiente: "amber", rechazada: "red", cancelada: "gray" };
  const STATUS_LABEL = { confirmada: "Confirmada", pendiente: "Pendiente de aprobación", rechazada: "Rechazada", cancelada: "Cancelada" };

  return (
    <div data-testid="sum-reservations-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h2 style={{ fontSize: 14, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: 0 }}>Reservas del {sumName}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="btn-ghost" onClick={() => shiftMonth(-1)} style={{ padding: "6px 10px" }} data-testid="sum-month-prev">←</button>
          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 90, textAlign: "center" }}>{month}</span>
          <button className="btn-ghost" onClick={() => shiftMonth(1)} style={{ padding: "6px 10px" }} data-testid="sum-month-next">→</button>
        </div>
      </div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {rows.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin reservas este mes.</div>}
        {rows.map((r) => (
          <div key={r.id} style={{ padding: "14px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }} data-testid={`sum-reservation-row-${r.id}`}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 900, fontSize: 14, color: "#0A0A0A" }}>{r.date} · {r.time_from} a {r.time_to} <span className={`badge ${STATUS_BADGE[r.status]}`} style={{ marginLeft: 6 }}>{STATUS_LABEL[r.status]}</span></div>
              <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 3 }}>Unidad {r.unit} · {r.requested_by_name} · {r.guests_count} invitados{r.reason ? ` · ${r.reason}` : ""}{r.site_name ? ` · ${r.site_name}` : ""}</div>
              {r.status === "rechazada" && r.reject_reason && <div style={{ fontSize: 11, color: "#BF3437", marginTop: 3 }}>Motivo del rechazo: {r.reject_reason}</div>}
            </div>
            {r.status === "pendiente" && (
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn-primary" disabled={busy === r.id} onClick={() => decide(r.id, true)} style={{ padding: "7px 12px", fontSize: 11 }} data-testid={`sum-approve-${r.id}`}>Aprobar</button>
                <button className="btn-ghost" disabled={busy === r.id} onClick={() => setRejecting(r.id)} style={{ padding: "7px 12px", fontSize: 11, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`sum-reject-${r.id}`}>Rechazar</button>
              </div>
            )}
            {(r.status === "confirmada" || r.status === "pendiente") && (
              <button className="btn-ghost" disabled={busy === r.id} onClick={() => cancel(r.id)} style={{ padding: "7px 12px", fontSize: 11 }} data-testid={`sum-cancel-${r.id}`}>Cancelar</button>
            )}
            {rejecting === r.id && (
              <div style={{ width: "100%", display: "flex", gap: 8, marginTop: 6 }}>
                <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motivo del rechazo (opcional)" style={{ flex: 1 }} data-testid={`sum-reject-reason-${r.id}`}/>
                <button className="btn-primary" disabled={busy === r.id} onClick={() => decide(r.id, false, rejectReason)} style={{ padding: "7px 12px", fontSize: 11, background: "#BF3437" }} data-testid={`sum-reject-confirm-${r.id}`}>Confirmar rechazo</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


